import React, { useEffect } from "react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { BridgeMark, BrandWordmark } from "./BridgeMotif";

const VALID = ["en", "es", "am"];
const CHOICES = [
  ["en", "English"],
  ["es", "Español"],
  ["am", "አማርኛ"],
];

// Handles two responsibilities for the language-preference system:
// 1. Applies the authenticated account's saved preferred language (source of truth).
// 2. Shows the first-visit language selector to brand-new anonymous visitors.
export function LanguageGate() {
  const { lang, changeLang, hasPreference, t } = useLang();
  const { user, loading } = useAuth();

  // Authenticated experience: the account's saved language is authoritative.
  // Missing/invalid preference safely resolves to English.
  useEffect(() => {
    if (user && typeof user === "object") {
      const pref = VALID.includes(user.lang) ? user.lang : "en";
      changeLang(pref);
    }
  }, [user, changeLang]);

  const show = !loading && !user && !hasPreference;
  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="language-gate-title"
      data-testid="language-gate"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slateink/60 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.25)]">
        <div className="flex items-center gap-2.5">
          <BridgeMark size={30} />
          <BrandWordmark className="text-xl" />
        </div>
        <h2 id="language-gate-title" data-testid="language-gate-title" className="mt-6 font-display text-2xl font-bold tracking-tight text-slateink">
          {t("langgate.title")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("langgate.subtitle")}</p>
        <div className="mt-6 grid gap-3">
          {CHOICES.map(([code, label], i) => (
            <button
              key={code}
              autoFocus={i === 0}
              data-testid={`language-gate-${code}`}
              onClick={() => changeLang(code)}
              className={`flex items-center justify-between rounded-xl border px-5 py-3.5 text-left text-base font-semibold transition-all hover:-translate-y-0.5 ${
                code === "en"
                  ? "border-terracotta bg-terracotta text-white hover:bg-terracotta-hover"
                  : "border-border bg-white text-slateink hover:border-terracotta/50 hover:bg-terracotta-light/40"
              }`}
            >
              <span>{label}</span>
              {code === "en" && <span className="text-xs font-medium uppercase tracking-wide opacity-80">{t("langgate.default")}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
